import { useCallback, useMemo, useState } from 'react';
import { Colors } from '~/themes/colors';
import { Column, ConfirmPopup, Pressable, Row, Text } from '~/commonComponents';
import Portal from '~/commonComponents/Portal';
import { InviteProfessionalPopup } from '~/commonComponents/InviteProfessional/Popup';
import { IUser } from '~/models';
import { useAppSelector } from '~/hooks/useAppSelector';
import PlusIcon from '../../../../../images/icons/PlusIcon';
import MailIcon from '../../../../../images/mail-solid.svg';
import CloseIcon from '../../../../../images/icons/CloseIcon';
import Tooltip from '../../../../Tooltip';
import { CollapsibleRow } from '../CollapsibleRow/CollapsibleRow';
import { Title } from '../CollapsibleRow/Title';

interface IProps {
  defaultProfessionals: string[];
  onConfirm: (values: string[]) => void;
  professionType?: string;
}

export const InviteRow = ({ defaultProfessionals = [], onConfirm, professionType }: IProps) => {
  const professionals = useAppSelector((state) => [
    ...(state.user.favoriteProfessionals ?? []),
    ...(state.user.recentProfessionals ?? []),
    ...(state.user.recommendedProfessionals ?? []),
  ]);

  const [isInvitePopupOpen, setInvitePopupOpen] = useState(false);
  const [deletePopup, setDeletePopup] = useState<{ open: boolean; professional: IUser | null }>({
    open: false,
    professional: null,
  });

  const selectedProfessionals = useMemo<IUser[]>(() => {
    return defaultProfessionals.map((id) => {
      const match: any = professionals.find((p: any) => p?.id === id || p?.professional?.id === id);

      const fullName =
        match?.fullname ?? match?.professional?.fullName ?? match?.professional?.full_name;

      return {
        id,
        full_name: fullName,
      } as IUser;
    });
  }, [professionals, defaultProfessionals]);

  const handleAddProfessional = () => {
    setInvitePopupOpen(true);
  };

  const handleRemoveProfessional = useCallback(
    (id: string) => {
      onConfirm(defaultProfessionals.filter((professionalId) => professionalId !== id));
    },
    [defaultProfessionals, onConfirm],
  );

  return (
    <>
      <CollapsibleRow
        // @ts-expect-error - missing prop
        disabled
        customCardStyles={{ borderColor: Colors.neutral_100 }}
        titlePosition="flex-start"
        TitleComponent={() => (
          <Row
            style={{
              alignItems: 'center',
            }}
          >
            <Title text="Invite:" />

            <Text color={Colors.neutral_500} marginLeft={4}>
              (Optional)
            </Text>

            <div
              style={{
                marginLeft: 4,
                position: 'relative',
                top: '2px',
              }}
            >
              {/* @ts-expect-error - missing prop */}
              <Tooltip content="Please note, invites will not be delivered to professionals on requested dates in which they are unavailable." />
            </div>
          </Row>
        )}
        ValueComponent={() => (
          <div style={{ marginRight: -16 }}>
            {selectedProfessionals?.length ? (
              <Column flex={1} alignItems="flex-end" gap={13}>
                <>
                  {selectedProfessionals.map((professional) => (
                    <Row key={professional.id} alignItems="center">
                      <Text marginRight={18} textAlign="right">
                        {professional.full_name}
                      </Text>
                      <Pressable
                        onPress={() => setDeletePopup({ open: true, professional: professional })}
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        <CloseIcon color={Colors.secondary_500} />
                      </Pressable>
                    </Row>
                  ))}
                  <Pressable onPress={handleAddProfessional}>
                    <Row alignItems="center">
                      <PlusIcon color={Colors.secondary_500} />
                      <Text bold fontSize={18} color="secondary_500" marginLeft={8}>
                        Invite More
                      </Text>
                    </Row>
                  </Pressable>
                </>
              </Column>
            ) : (
              <Pressable onPress={handleAddProfessional}>
                <Row>
                  <Text color="neutral_300" marginRight={14}>
                    Add Professionals
                  </Text>
                  <PlusIcon color={Colors.secondary_500} />
                </Row>
              </Pressable>
            )}
          </div>
        )}
        icon={MailIcon}
      />

      <Portal>
        {deletePopup.open && deletePopup.professional && (
          // @ts-expect-error - ignore attribute missing
          <ConfirmPopup
            title="Remove Professional From Invite"
            description={
              <>
                Are you sure you would like to remove
                <strong>&nbsp;{deletePopup.professional!.full_name}&nbsp;</strong>
                from your request?
              </>
            }
            descriptionStyle={{ width: '95%', marginBottom: 50 }}
            leftButtonText="Go Back"
            leftButtonStyle={{
              backgroundColor: Colors.secondary_500,
            }}
            closePopup={() => setDeletePopup({ open: false, professional: null })}
            leftButtonAction={() => setDeletePopup({ open: false, professional: null })}
            rightButtonText="Remove"
            rightButtonAction={() => {
              handleRemoveProfessional(deletePopup.professional!.id);
              setDeletePopup({ open: false, professional: null });
            }}
          />
        )}

        <div onClick={(e) => e.stopPropagation()}>
          <InviteProfessionalPopup
            isOpen={isInvitePopupOpen}
            isRefetchJob={false}
            defaultProfessionals={defaultProfessionals}
            profession={professionType}
            onClose={() => {
              setInvitePopupOpen(false);
            }}
            onConfirm={(ids) => {
              setInvitePopupOpen(false);
              onConfirm(ids);
            }}
          />
        </div>
      </Portal>
    </>
  );
};
